import React, { useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import useStyles from '../../theme/useStyles';

// Replaces window.prompt() — used for "why are you voiding this record?".
// window.prompt blocks the thread, can't be styled, and is blocked outright
// in some embedded browsers.
//
//   <PromptDialog
//     open={!!voiding}
//     title="Delete Record ATT-1004"
//     message="Please enter the reason for deletion:"
//     onSubmit={(reason) => voidRecord(voiding, reason)}
//     onCancel={() => setVoiding(null)}
//   />
export interface PromptDialogProps {
  open?: boolean;
  title?: string;
  message?: ReactNode;
  placeholder?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Blocks submit while empty. Defaults to true. */
  required?: boolean;
  requiredError?: string;
  multiline?: boolean;
  destructive?: boolean;
  /** Receives the trimmed input. */
  onSubmit?: (value: string) => void;
  onCancel?: () => void;
  isDarkMode?: boolean;
  accent?: string;
}

const PromptDialog = ({
  open = false,
  title = 'Please confirm',
  message,
  placeholder = '',
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  required = true,
  requiredError = 'A reason is required.',
  multiline = true,
  destructive = true,
  onSubmit,
  onCancel,
  isDarkMode,
  accent
}: PromptDialogProps) => {
  const s = useStyles(isDarkMode, accent);
  const [value, setValue] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) { setValue(''); setError(''); }
  }, [open]);

  if (!open) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmed = value.trim();
    if (required && !trimmed) {
      setError(requiredError);
      return;
    }
    onSubmit?.(trimmed);
  };

  const Control = multiline ? 'textarea' : 'input';

  return (
    <div style={s.notificationOverlay} onClick={onCancel}>
      <form
        onSubmit={handleSubmit}
        style={{
          ...s.notificationBox,
          textAlign: 'left',
          minWidth: '360px',
          borderTop: `5px solid ${destructive ? s.t.danger : s.t.primary}`
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h4 style={s.notificationTitle}>{title}</h4>
        {message && (
          <p style={{ ...s.notificationText, marginBottom: '12px', fontSize: s.t.fontSize.md }}>{message}</p>
        )}

        <Control
          autoFocus
          value={value}
          rows={multiline ? 3 : undefined}
          placeholder={placeholder}
          onChange={(e) => { setValue(e.target.value); if (error) setError(''); }}
          style={{
            ...s.input,
            marginBottom: error ? '6px' : '20px',
            ...(multiline ? { resize: 'vertical', fontFamily: 'inherit' } : {}),
            ...(error ? { borderColor: s.t.error } : {})
          }}
        />
        {error && (
          <div style={{ color: s.t.error, fontSize: s.t.fontSize.sm, marginBottom: '14px' }}>{error}</div>
        )}

        <div style={{ display: 'flex', gap: '10px' }}>
          <button type="button" style={{ ...s.viewBtn, flex: 1, padding: '10px' }} onClick={onCancel}>
            {cancelLabel}
          </button>
          <button
            type="submit"
            style={{ ...(destructive ? s.dangerBtn : s.submitBtn), flex: 1, padding: '10px', fontSize: '15px' }}
          >
            {confirmLabel}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PromptDialog;
